import React from "react";
import { MapPin, Phone, Clock, ChevronLeft } from "lucide-react";
import { useRestaurant } from "./context/RestaurantContext";
import useNav from "./hooks/useNav";

const AboutPage = () => {
    const { restaurant } = useRestaurant();
    const navigate = useNav();

    if (!restaurant) return null;

    const hoursStart = restaurant.settings?.working_hours_start;
    const hoursEnd   = restaurant.settings?.working_hours_end;
    const hasHours   = hoursStart && hoursEnd;

    return (
        <div className="min-h-screen bg-gray-100 pb-10">

            {/* Кнопка назад */}
            <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-100 px-4 h-12 flex items-center gap-3 shadow-sm">
                <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 transition">
                    <ChevronLeft size={22} />
                </button>
                <span className="font-semibold text-gray-800 text-[15px]">О ресторане</span>
            </div>

            {/* Hero */}
            <div className="bg-white px-6 pt-8 pb-6 flex flex-col items-center text-center border-b border-gray-100">
                {restaurant.logo && (
                    <div className="relative mb-5">
                        {/* Декоративный круг за логотипом */}
                        <div className="absolute inset-0 -m-4 rounded-full bg-brandBlue/8" />
                        <img
                            src={restaurant.logo}
                            alt={restaurant.name}
                            className="relative h-24 w-auto max-w-[180px] object-contain"
                            style={{ filter: 'drop-shadow(0 4px 16px rgba(62,125,174,0.2))' }}
                            onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
                        />
                    </div>
                )}
                <h1 className="font-playfair text-2xl font-semibold text-gray-900 tracking-wide">
                    {restaurant.name}
                </h1>
                {/* Акцентная линия */}
                <div className="mt-2 mb-3 w-10 h-0.5 rounded-full bg-brandBlue/50" />
                {restaurant.description && (
                    <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
                        {restaurant.description}
                    </p>
                )}
            </div>

            <div className="max-w-screen-md mx-auto px-4 pt-4 space-y-3">

                {/* Контакты */}
                {(restaurant.address || restaurant.phone) && (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-100">
                        <div className="px-5 py-3 bg-gray-50">
                            <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Контакты</span>
                        </div>
                        {restaurant.address && (
                            <div className="flex items-start gap-3 px-5 py-4">
                                <MapPin size={18} className="text-brandBlue/70 mt-0.5 shrink-0" />
                                <span className="text-sm text-gray-700 leading-snug">{restaurant.address}</span>
                            </div>
                        )}
                        {restaurant.phone && (
                            <a
                                href={`tel:${restaurant.phone}`}
                                className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 active:bg-gray-100 transition"
                            >
                                <Phone size={18} className="text-brandBlue/70 shrink-0" />
                                <span className="text-sm text-gray-700">{restaurant.phone}</span>
                            </a>
                        )}
                    </div>
                )}

                {/* Часы работы */}
                {hasHours && (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-5 py-3 bg-gray-50 flex items-center gap-2 border-b border-gray-100">
                            <Clock size={13} className="text-gray-400" />
                            <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Часы работы</span>
                        </div>
                        <div className="flex items-center justify-between px-5 py-4">
                            <span className="text-sm text-gray-500">Ежедневно</span>
                            <span className="text-sm font-semibold text-gray-800">{hoursStart} – {hoursEnd}</span>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AboutPage;
